/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import { type DocNode } from '@atlaskit/adf-schema';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { FadeIn } from '@atlaskit/motion';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { useAgentsPaginatedByAgentId } from '@atlassian/conversation-assistant-agent/controllers/agents';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { RovoChatPromptInput } from '@atlassian/conversation-assistant-chat-prompt-input/RovoChatPromptInput';
import {
	type AiFeatureContextOverride,
	type SelectedAgentChangePayload,
} from '@atlassian/conversation-assistant-chat-prompt-input/types';
import type { MessageAgentIdentity } from '@atlassian/conversation-assistant-chat-prompt-input/ui/chat-prompt-input/message-agent-identity';
import type {
	DictationOverlayBackground,
	ChatPromptInputRef,
} from '@atlassian/conversation-assistant-chat-prompt-input/ui/chat-prompt-input/types';
import type { UploadedFile } from '@atlassian/conversation-assistant-chat-prompt-input/UploadedFile';
import { useChatModeStore } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStore';
import {
	AUTOSUGGEST_SOURCE,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type SkillSelectionSource } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import type { Dialogues } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	CHAT_STORE_STATUS,
	isStoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	useStagingAreaActions,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	useChatInputUISelector,
	useEmptyChatLaunchSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';
import { useSpaces } from '@atlassian/rovo-spaces/controllers/space-store';
import { useAddSpaceSource } from '@atlassian/rovo-spaces/services/useAddSpaceSource';
import { useGetSpace } from '@atlassian/rovo-spaces/services/useGetSpace';
import { SourceLinkPickerPopup } from '@atlassian/rovo-spaces/ui/SpaceLandingPage/SourceLinkPickerPopup';
import type { SpaceSource } from '@atlassian/rovo-spaces/ui/SpaceLandingPage/types';
import { useSpaceSourcePlugins } from '@atlassian/rovo-spaces/ui/SpaceLandingPage/useSpaceSourcePlugins';

import { useFileThumbnails } from '../../common/hooks/useFileThumbnails';
import { usePromptAppConnectors } from '../../common/hooks/usePromptAppConnectors';
import { interactionMetrics } from '../../common/utils/browser-metrics/interactionMetrics';
import { extractPlainTextFromAdf } from '../../common/utils/content-features/extractPlainTextFromAdf';
import { getPromptCardSendType } from '../../common/utils/content-features/getPromptCardSendType';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useFullScreenInputStaggerAnimation } from '../../common/utils/use-animation-delay/useFullScreenInputStaggerAnimation';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useWindowLocation } from '../../common/utils/use-window-location/useWindowLocation';
import { useActiveAction } from '../../controllers/active-action';
import { createSeededConversationFromAction } from '../../controllers/active-action/create-seeded-conversation-from-action';
import { DEFAULT_AGENT } from '../../controllers/agents/constants';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../controllers/conversation/useConversation';
import { usePromptCardSendTrackingActions } from '../../controllers/prompt-card-send-tracking/usePromptCardSendTrackingActions';
import type { ConversationAssistantUIModes } from '../../types';
import { usePlaceholderOverrideValue } from '../chat-input-placeholder-override-context/usePlaceholderOverrideValue';

import { type AgentSelectionCandidate } from './AgentSelectionCandidate';
import { isDefaultAgentSelection } from './isDefaultAgentSelection';
import messages from './messages';
import { QueuedMessageBanner } from './QueuedMessageBanner';
import { SBSTriggerEffect, type InterceptSubmitFn } from './SBSTriggerEffect';
import { useAutocompleteEditorContext } from './useAutocompleteEditorContext';
import { useInputHatSelectedAgent } from './useInputHatSelectedAgent';
import { useFetchCustomFollowUpContext } from './utils';

//Two minutes
const INPUT_TYPING_SESSION_MAX_DURATION = 2 * 60 * 1000;

// Stable empty list so unenrolled users skip the per-render message scan.
const NO_AUTOCOMPLETE_MESSAGES: StoreChatItem[] = [];
const CHAT_INPUT_INTERACTION_SOURCE: InteractionSource = 'chatInput';

const getSelectedAgentOverrideId = ({
	nextMessageSelectedAgentId,
	currentAgent,
}: {
	nextMessageSelectedAgentId: string | undefined;
	currentAgent: AgentSelectionCandidate | undefined;
}): string | undefined => {
	if (!nextMessageSelectedAgentId) {
		return undefined;
	}

	if (
		nextMessageSelectedAgentId === currentAgent?.id ||
		nextMessageSelectedAgentId === DEFAULT_AGENT.id
	) {
		return undefined;
	}

	return nextMessageSelectedAgentId;
};

const getSendMessageAgentParam = <TAgent extends AgentSelectionCandidate>({
	selectedAgentOverrideId,
	currentAgent,
}: {
	selectedAgentOverrideId: string | undefined;
	currentAgent: TAgent | undefined;
}): { agentId: string; agent?: undefined } | { agent: TAgent | undefined; agentId?: undefined } => {
	if (selectedAgentOverrideId) {
		return { agentId: selectedAgentOverrideId };
	}

	return { agent: currentAgent };
};

export type ChatInputProps = {
	mode?: ConversationAssistantUIModes;
	suppressInitialFocus?: boolean;
	isDisabled?: boolean;
	showActionsButton?: boolean;
	conversationId: string;
	enableVoiceDictation?: boolean;
	editorPopupsMountPoint?: HTMLDivElement;
	editorPopupsBoundariesElement?: HTMLDivElement;
	shouldRenderQuickActionsInPortal?: boolean;
	isExpanded?: boolean;
	activeAction?: ConversationAction;
	onDiscoverSkillsClick?: () => void;
	onBrowsePromptLibraryClick?: () => void;
	onCreateSpaceClick?: () => void;
	onAutoSuggestQueryChange?: (query: string) => void;
	onSubmitOverride?: (prompt: DocNode) => void;
	placeholderOverride?: string;
	enableSkills?: boolean;
	enableAgentMentions?: boolean;
	initialPromptText?: string;
	shouldSeedInitialPromptText?: boolean;
	dictationOverlayBackground?: DictationOverlayBackground;
};

type ChatInputInnerProps = ChatInputProps & {
	isChatOnboardingMiniModalOpen: boolean;
	selectedSpace: ReturnType<typeof useChatInputUISelector>[0]['selectedSpace'];
	smartLink3PPostAuth: ReturnType<typeof useChatInputUISelector>[0]['smartLink3PPostAuth'];
};

type ChatInputSubmitMode = {
	deepResearchEnabled: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: 'MARATHON' | 'LONG_HORIZON';
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	webSearchEnabled: boolean;
	appFilters?: RovoApp[];
	addCurrentPageAsContext: boolean;
	shouldExtractConversationScopes: boolean;
	skillSelectionSource?: SkillSelectionSource;
	inputSelectedAgentId?: string;
	messageAgentIdentity?: MessageAgentIdentity;
};

type QueuedMessage = {
	id: number;
	prompt: DocNode;
	mode: ChatInputSubmitMode;
	files?: UploadedFile[];
	preview: string;
};

const ChatInputComponent = (props: ChatInputInnerProps): JSX.Element => {
	di(useChatStoreActions, useConversation, useUserPreferences, useAgentsPaginatedByAgentId);
	const fullscreenInputStaggerAnimation = useFullScreenInputStaggerAnimation();
	const {
		conversationId,
		activeAction,
		onSubmitOverride,
		placeholderOverride: propPlaceholderOverride,
		suppressInitialFocus,
		initialPromptText,
		shouldSeedInitialPromptText,
		isChatOnboardingMiniModalOpen,
		selectedSpace,
		smartLink3PPostAuth,
	} = props;

	const { productKey, productHeader, siteId, orgId, experienceId } = useGlobalConfig();
	const { setIsChatOnboardingMiniModalOpen } = useUIStoreActions();

	const {
		fullScreen,
		elements,
		promptInputDroppableUrls,
		enableBrowserContextBanner,
		browserContextSanitizer,
	} = useUIConfig();

	// Derive entity context override from Smart Link post-auth or empty-launch store for
	// entity-driven launches. When present, the context banner shows this entity instead of
	// the current page and sends only entity context to the backend.
	const [
		{
			projectContext: emptyLaunchProjectContext,
			interactionSource: emptyLaunchInteractionSource,
			invokedFrom: emptyLaunchInvokedFrom,
		},
	] = useEmptyChatLaunchSelector();
	const smartLink3PPostAuthIsActive =
		fg('platform_sl_3p_post_auth_chat_open_fg') && smartLink3PPostAuth?.isActive;
	const smartLink3PPostAuthProjectContext = smartLink3PPostAuthIsActive
		? smartLink3PPostAuth.projectContext
		: undefined;
	const aiFeatureContextOverride: AiFeatureContextOverride | undefined = useMemo(() => {
		const projectContext = smartLink3PPostAuthProjectContext ?? emptyLaunchProjectContext;
		const override = projectContext
			? {
					entityContext: {
						url: projectContext.projectUrl,
						name: projectContext.projectName,
					},
				}
			: undefined;
		return override;
	}, [emptyLaunchProjectContext, smartLink3PPostAuthProjectContext]);
	const useCurrentPageContextForInput =
		elements?.shouldShowContextBanner !== false &&
		!elements?.stagingAreaEnabled &&
		!isChatOnboardingMiniModalOpen &&
		!smartLink3PPostAuthIsActive;
	const { conversation, selectedAgent } = useConversation({
		conversationId: props.conversationId,
	});
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'chatInput',
	});
	type ActiveSelectedAgentState =
		| ({ conversationId: string } & SelectedAgentChangePayload)
		| { conversationId: string; id?: undefined; identityAccountId?: undefined };
	const [activeSelectedAgentState, setActiveSelectedAgentState] =
		useState<ActiveSelectedAgentState>();
	const { formatMessage } = useIntl();

	const { sendMessage, abortRequests, createSeededConversation } = useChatStoreActions();
	const isSpaceScopedConversation = Boolean(conversation?.rovoSpaceId || selectedSpace);
	const spaces = useSpaces();
	const activeRovoSpace = spaces.find((s) =>
		[conversation?.rovoSpaceId, selectedSpace?.id].includes(s.id),
	);

	// The active space ID is derived directly from the conversation or selected space — not from
	// the store — because the SpaceStore may still be loading when this component first renders
	// (e.g. on reload with a space already selected), meaning activeRovoSpace would be undefined
	// even though a space IS selected.
	const activeSpaceId = conversation?.rovoSpaceId ?? selectedSpace?.id ?? '';

	// Fetch individual space details when the store doesn't yet have sources for the active space.
	// This ensures SpaceContextBanner shows the correct source count on reload, before
	// the space list fetch completes and populates the store.
	const { space: fetchedSpace } = useGetSpace({
		spaceId: activeSpaceId,
		cloudId: siteId,
		product: productKey,
		experienceId,
		enabled:
			Boolean(activeSpaceId) &&
			(activeRovoSpace === undefined || activeRovoSpace.sources === undefined),
	});
	const activeSpaceSources = useMemo(
		() => activeRovoSpace?.sources ?? fetchedSpace?.sources,
		[activeRovoSpace?.sources, fetchedSpace?.sources],
	);
	const areActiveSpaceSourcesKnown = activeSpaceSources !== undefined;
	const activeSpaceSourceOptions = useMemo<SpaceSource[]>(
		() =>
			(activeSpaceSources ?? []).map((source) => ({
				id: source.id,
				url: source.url,
				title: source.name ?? source.url,
				sourceType: source.sourceType as SpaceSource['sourceType'],
			})),
		[activeSpaceSources],
	);
	const spaceSourcePlugins = useSpaceSourcePlugins(siteId);
	const { addSource } = useAddSpaceSource({
		spaceId: activeSpaceId,
		cloudId: siteId,
		product: productKey,
		experienceId,
	});
	const handleZeroSourcesSourceAdded = useCallback(
		(source: SpaceSource) => {
			void addSource({
				url: source.url,
				ari: source.id,
				name: source.title,
				sourceType: source.sourceType as SpaceSource['sourceType'],
			});
		},
		[addSource],
	);
	const zeroSourcesAction = useMemo(
		() =>
			isSpaceScopedConversation && areActiveSpaceSourcesKnown && activeSpaceSources.length === 0 ? (
				<SourceLinkPickerPopup
					plugins={spaceSourcePlugins}
					cloudId={siteId}
					sources={activeSpaceSourceOptions}
					onSourceAdded={handleZeroSourcesSourceAdded}
					trigger={({ ref, onClick, ...triggerProps }) => (
						<span ref={ref} {...triggerProps}>
							<Button
								appearance="primary"
								onClick={onClick}
								testId="zero-sources-banner-add-sources-button"
								shouldFitContainer
							>
								{formatMessage(messages.chatInputAddSourcesButton)}
							</Button>
						</span>
					)}
				/>
			) : undefined,
		[
			activeSpaceSourceOptions,
			areActiveSpaceSourcesKnown,
			activeSpaceSources,
			formatMessage,
			handleZeroSourcesSourceAdded,
			isSpaceScopedConversation,
			siteId,
			spaceSourcePlugins,
		],
	);

	const sbsInterceptSubmitRef = useRef<InterceptSubmitFn | null>(null);
	const windowLocation = useWindowLocation();
	const { sendAnalyticsEvent } = useAnalytics();
	const { takePromptCardSource } = usePromptCardSendTrackingActions();
	const { preferences } = useUserPreferences();
	const { getCustomFollowUpContext } = useFetchCustomFollowUpContext(
		activeAction?.customFollowUpContext?.fetch,
	);
	const { setActiveAction } = useActiveAction();
	const { stagingAreaMode, promptAppConnectorSources } = useStagingAreaState();
	const { setPromptAppConnectorSources } = useStagingAreaActions();
	const { handlePromptAppConnectors } = usePromptAppConnectors({
		conversationId,
		assistanceServiceParams,
	});

	// A 3P suggested prompt staged from the Create-with-Rovo modal carries its
	// connector sources here; surface the app-connection banner for them (with the
	// per-row Connected state on), then clear so it fires once per staged prompt.
	useEffect(() => {
		if (promptAppConnectorSources.length === 0) {
			return;
		}
		handlePromptAppConnectors(promptAppConnectorSources, { showConnectedState: true });
		setPromptAppConnectorSources([]);
	}, [promptAppConnectorSources, handlePromptAppConnectors, setPromptAppConnectorSources]);

	const [promptInputMetricsState, setPromptInputMetricsState] = useState<{
		lastInteractionTime: number | null;
		isFirstInitiation: boolean;
	}>({
		lastInteractionTime: null,
		isFirstInitiation: true,
	});

	const { cacheFiles } = useFileThumbnails([]);
	const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([]);
	const queueIdCounterRef = useRef(0);

	const isAgentTyping =
		conversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
		conversation?.status === CHAT_STORE_STATUS.typingResponse;

	const shouldMinimizeInputHeight = props.isExpanded && conversation?.id === EMPTY_CONVERSATION_ID;

	const contentContext: ContentContext = elements?.stagingAreaEnabled ? 'staging-area' : 'global';
	const isStudioSkillsInStagingAreaEnabled =
		productHeader === 'atlassian-studio' && fg('rovo_chat_studio_skills_in_staging_area');
	const resolvedEnableSkills = isStudioSkillsInStagingAreaEnabled ? true : props.enableSkills;
	const isDefaultAgent = isDefaultAgentSelection(selectedAgent);

	const [storeMessages] = useChatMessages({ conversationId });

	const shouldEvaluateTypeaheadExperiment = !storeMessages.length || !conversation;
	const shouldUseTypeahead =
		shouldEvaluateTypeaheadExperiment &&
		FeatureGates.getExperimentValue('rovo_chat_typeahead', 'isEnabled', false);
	const userMessagesForEditor = useMemo(() => {
		return storeMessages.filter(isStoreHuman).map((message) => ({
			id: message.content.id,
			content: message.content.content,
		}));
	}, [storeMessages]);
	// Exposure is fired by the preset's gate read; suppress here to avoid double-counting.
	const isAutocompleteEnabled = FeatureGates.getExperimentValue(
		'platform_editor_ai_autocomplete_rovo_chat_editor',
		'isEnabled',
		false,
		{ fireExperimentExposure: false },
	);
	const autocompleteContext = useAutocompleteEditorContext(
		isAutocompleteEnabled ? storeMessages : NO_AUTOCOMPLETE_MESSAGES,
	);

	const contextPlaceholderOverride = usePlaceholderOverrideValue();

	const [chatModeState] = useChatModeStore(conversationId);
	const enableMaxMode = elements?.maxModeButton ?? true;
	const isTaskModeEnabled = enableMaxMode && chatModeState.taskModeEnabled;

	const placeholderOverride = useMemo(() => {
		if (propPlaceholderOverride) {
			return propPlaceholderOverride;
		}

		if (isTaskModeEnabled) {
			return formatMessage(messages.chatInputTaskModePlaceholder);
		}

		if (chatModeState.deepResearchEnabled && fg('rovo_chat_enable_tasks')) {
			return formatMessage(messages.deepResearchPlaceholder);
		}

		const isNotEmptyConversation = conversationId && conversationId !== EMPTY_CONVERSATION_ID;

		if (contextPlaceholderOverride) {
			return contextPlaceholderOverride;
		}
		if (isNotEmptyConversation) {
			return formatMessage(messages.chatInputContinueConversationPlaceholder);
		}

		return FeatureGates.getExperimentValue(
			'rovo-chat-input-placeholder-updated',
			'isEnabled',
			false,
		)
			? formatMessage(messages.chatInputDefaultPlaceholderUpdated)
			: formatMessage(messages.chatInputDefaultPlaceholder);
	}, [
		conversationId,
		formatMessage,
		contextPlaceholderOverride,
		propPlaceholderOverride,
		isTaskModeEnabled,
		chatModeState.deepResearchEnabled,
	]);

	const sendSeededConversationAndCleanup = useCallback(
		async (dialogues: Dialogues[]) => {
			const newConversationId = await createSeededConversationFromAction({
				action: activeAction,
				dialogues,
				createSeededConversation,
				fallbackName: dialogues[0].human_message.content, // leverage the canned prompt from the action
				sourceId: conversation?.sourceId,
				id: conversationId,
				defaultFollowUpSource: 'ai-smart-button-dynamic-surface',
				agentId: selectedAgent?.id,
			});

			if (!newConversationId) {
				return;
			}

			// Engage succeeds via `onCreated`; only remove the view, not dismiss state.
			setActiveAction(undefined);
		},
		[
			activeAction,
			conversation?.sourceId,
			conversationId,
			createSeededConversation,
			selectedAgent?.id,
			setActiveAction,
		],
	);

	const onPromptChange = useCallback(() => {
		//Setting up ai-mate.message-composition-time interaction metric

		const { isFirstInitiation, lastInteractionTime } = promptInputMetricsState;
		const now = Date.now();
		const isInactive =
			!lastInteractionTime || now - lastInteractionTime > INPUT_TYPING_SESSION_MAX_DURATION;

		if (isInactive) {
			interactionMetrics.messageCompositionTime.cancel();
			setPromptInputMetricsState({ lastInteractionTime: now, isFirstInitiation: true });
			return;
		}

		if (isFirstInitiation) {
			interactionMetrics.messageCompositionTime.start();
			setPromptInputMetricsState((prevState) => ({ ...prevState, isFirstInitiation: false }));
		}
	}, [promptInputMetricsState, setPromptInputMetricsState]);

	const normalizePromptText = useCallback((text: string) => text.trim().replace(/\s+/g, ' '), []);
	const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
	// Preserve the old gate-derived default for integrations that omit the prop,
	// while allowing an explicit false to opt out of agent mentions.
	const isAgentSelectionEnabled =
		props.enableAgentMentions !== false && isMentionAgentsExperimentEnabled;
	const activeSelectedAgentId =
		activeSelectedAgentState?.conversationId === conversationId
			? activeSelectedAgentState.id
			: undefined;
	const handleSelectedAgentChange = useCallback(
		(agent: SelectedAgentChangePayload | undefined) => {
			setActiveSelectedAgentState(
				agent
					? {
							conversationId,
							id: agent.id,
							identityAccountId: agent.identityAccountId,
						}
					: { conversationId },
			);
		},
		[conversationId],
	);
	const selectedAgentForInputHat = useInputHatSelectedAgent({
		conversationId,
		activeSelectedAgentId,
		selectedAgentIdentityAccountId:
			activeSelectedAgentState?.conversationId === conversationId
				? activeSelectedAgentState.identityAccountId
				: undefined,
	});
	const getSelectedAgentOverrideForId = useCallback(
		(inputSelectedAgentId: string | undefined) =>
			isAgentSelectionEnabled
				? getSelectedAgentOverrideId({
						nextMessageSelectedAgentId: inputSelectedAgentId,
						currentAgent: selectedAgent,
					})
				: undefined,
		[isAgentSelectionEnabled, selectedAgent],
	);
	const selectedAgentOverrideId = getSelectedAgentOverrideForId(activeSelectedAgentId);

	const onSubmit = useCallback(
		async (prompt: DocNode, mode: ChatInputSubmitMode, files?: UploadedFile[]) => {
			if (onSubmitOverride) {
				onSubmitOverride(prompt);
				return;
			}

			if (isChatOnboardingMiniModalOpen) {
				setIsChatOnboardingMiniModalOpen(false);
			}

			const promptCardSource = takePromptCardSource();

			if (
				isAgentTyping &&
				!activeAction?.customFollowUpContext?.available &&
				fg('rovo_chat_message_queue')
			) {
				const preview = extractPlainTextFromAdf(prompt).trim();
				const nextId = (queueIdCounterRef.current += 1);
				setMessageQueue((prev) => [
					...prev,
					{
						id: nextId,
						prompt,
						mode,
						files,
						preview,
					},
				]);
				return;
			}

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sendMessageButton',
				attributes: {
					agentId: selectedAgentOverrideId ?? selectedAgent?.id,
				},
			});

			if (activeAction?.customFollowUpContext?.available) {
				const dialogues = await getCustomFollowUpContext();
				dialogues && (await sendSeededConversationAndCleanup(dialogues));
			}

			if (files) {
				cacheFiles(
					files
						.filter((file) => file.fileObject && file.id)
						.map((file) => ({ id: file.id, fileObject: file.fileObject! })),
				);
			}

			const submittedSelectedAgentId = mode.inputSelectedAgentId;
			const submittedMessageAgentIdentity = mode.messageAgentIdentity;
			const submittedSelectedAgentOverrideId =
				getSelectedAgentOverrideForId(submittedSelectedAgentId);
			const submittedSendMessageAgentParam = getSendMessageAgentParam({
				selectedAgentOverrideId: submittedSelectedAgentOverrideId,
				currentAgent: selectedAgent,
			});

			const doSendMessage = (): void => {
				// `mode.skillSelectionSource` was captured by the chat-prompt-input from
				// the AnalyticsPropertiesContext at submit time and cleared there as well,
				// so this layer can simply forward the value without any additional cleanup.

				// When the chat is scoped to a Rovo Space, include the spaceId in every
				// sendMessage call so the backend can contextualise the conversation.
				const activeSpaceId = selectedSpace?.id ?? conversation?.rovoSpaceId;
				const resolvedSpaceId =
					activeSpaceId &&
					FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
						? activeSpaceId
						: undefined;
				const spaceContext = resolvedSpaceId
					? {
							spaceId: resolvedSpaceId,
						}
					: {};
				sendMessage({
					prompt,
					...submittedSendMessageAgentParam,
					...(isAgentSelectionEnabled && submittedSelectedAgentId
						? {
								inputSelectedAgentIds: [submittedSelectedAgentId],
								messageAgentIdentity: submittedMessageAgentIdentity,
							}
						: {}),
					// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
					// value when BE user settings are fully rolled out; BE overrides it via the capability header.
					storeUserMessages: preferences.storeUserMessages,
					messageSources: {
						interactionSource: emptyLaunchInteractionSource ?? CHAT_INPUT_INTERACTION_SOURCE,
						invokedFrom: emptyLaunchInvokedFrom,
					},
					files,
					thinkDeeperEnabled: mode.thinkDeeperEnabled,
					alphaExtendedThinkingMode: mode.alphaExtendedThinkingMode,
					deepResearchEnabled: mode.deepResearchEnabled,
					fastModeEnabled: mode.fastModeEnabled,
					taskModeEnabled: mode.taskModeEnabled,
					webSearchEnabled: mode.webSearchEnabled,
					appFilters: mode.appFilters,
					// Suppress current page context during entity-driven launches so the
					// backend only receives the entity's project context, not the current page.
					addCurrentPageAsContextUserPreference: aiFeatureContextOverride
						? false
						: mode.addCurrentPageAsContext,
					productKey,
					shouldExtractConversationScopes: mode.shouldExtractConversationScopes,
					skillSelectionSource: mode.skillSelectionSource,
					...spaceContext,
				});
			};

			// SBS evaluation trigger — checks if this query should be held for SBS consent.
			// If SBS triggers, the query is held until the user responds to the consent modal.
			// On consent/decline/opt-out, the held query is released and doSendMessage is called.
			const queryHeld = sbsInterceptSubmitRef.current?.(
				{
					prompt,
					mode,
					conversationId,
					siteId,
					productKey,
					existingMessageCount: storeMessages.length,
					// Pass the current page URL as implicit context when the user has "add current page
					// as context" enabled. This allows the SBS system to prepend "Context: <url>" to
					// the query sent to competitors and the Rovo Chrome Extension, giving them the same
					// page context that Rovo receives implicitly via the AI feature context system.
					implicitContextUrl: mode.addCurrentPageAsContext ? windowLocation : undefined,
				},
				doSendMessage,
			);

			if (!queryHeld) {
				// Normal flow — no SBS, submit immediately
				doSendMessage();
			}

			if (promptCardSource) {
				const sentText = normalizePromptText(extractPlainTextFromAdf(prompt));
				const originalText = normalizePromptText(promptCardSource.prompt);
				const sendType = getPromptCardSendType(sentText, originalText);
				if (sendType !== null) {
					const actionSubjectId =
						sendType === 'asIs'
							? 'promptCardSentAsIs'
							: sendType === 'placeholdersFilled'
								? 'promptCardSentWithPlaceholdersFilled'
								: 'promptCardSentTweaked';
					sendAnalyticsEvent({
						action: 'sent',
						actionSubject: 'prompt',
						actionSubjectId,
						attributes: {
							cardId: promptCardSource.cardId,
							sendType,
							...(promptCardSource.promptSource != null && {
								promptSource: promptCardSource.promptSource,
							}),
							...(promptCardSource.isFavorited != null && {
								isFavorited: promptCardSource.isFavorited,
							}),
						},
					});
				}
			}

			const now = Date.now();
			const isInactive =
				!promptInputMetricsState.lastInteractionTime ||
				now - promptInputMetricsState.lastInteractionTime > INPUT_TYPING_SESSION_MAX_DURATION;
			//In the edge case that someone waits over two minutes to submit their prompt after not typing
			if (!isInactive) {
				interactionMetrics.messageCompositionTime.stop();
				setPromptInputMetricsState((prevState) => ({ ...prevState, isFirstInitiation: true }));
			}
		},
		[
			selectedAgentOverrideId,
			selectedAgent,
			getSelectedAgentOverrideForId,
			isAgentSelectionEnabled,
			preferences.storeUserMessages,
			sendAnalyticsEvent,
			sendMessage,
			productKey,
			siteId,
			conversationId,
			activeAction?.customFollowUpContext,
			getCustomFollowUpContext,
			sendSeededConversationAndCleanup,
			cacheFiles,
			promptInputMetricsState.lastInteractionTime,
			setPromptInputMetricsState,
			takePromptCardSource,
			normalizePromptText,
			setIsChatOnboardingMiniModalOpen,
			storeMessages.length,
			aiFeatureContextOverride,
			windowLocation,
			onSubmitOverride,
			selectedSpace,
			conversation?.rovoSpaceId,
			isAgentTyping,
			emptyLaunchInteractionSource,
			emptyLaunchInvokedFrom,
			isChatOnboardingMiniModalOpen,
		],
	);

	const onAbort = useCallback(
		(
			mode: {
				deepResearchEnabled: boolean;
				thinkDeeperEnabled?: boolean;
				webSearchEnabled: boolean;
				taskModeEnabled?: boolean;
			},
			analyticsEvent: UIAnalyticsEvent | undefined,
		) => {
			if (analyticsEvent) {
				sendAnalyticsEvent(analyticsEvent.update({ actionSubjectId: 'abortRequestsButton' }));
			}
			abortRequests({
				conversationId,
				deepResearchEnabled: mode.deepResearchEnabled,
				thinkDeeperEnabled: mode.thinkDeeperEnabled,
				taskModeEnabled: mode.taskModeEnabled,
			});
		},
		[abortRequests, conversationId, sendAnalyticsEvent],
	);

	useEffect(() => {
		setMessageQueue([]);
	}, [conversation?.id]);

	const prevIsAgentTypingRef = useRef(isAgentTyping);
	useEffect(() => {
		const wasTyping = prevIsAgentTypingRef.current;
		prevIsAgentTypingRef.current = isAgentTyping;

		if (!fg('rovo_chat_message_queue')) {
			return;
		}

		if (wasTyping && !isAgentTyping && messageQueue.length > 0) {
			const [next, ...rest] = messageQueue;
			setMessageQueue(rest);
			void onSubmit(next.prompt, next.mode, next.files);
		}
	}, [isAgentTyping, messageQueue, onSubmit]);

	const chatInputRef = useRef<ChatPromptInputRef>(null);
	const suppressInitialFocusRef = useRef(suppressInitialFocus);
	suppressInitialFocusRef.current = suppressInitialFocus;
	// Trigger refocus on the chat input when changing conversations (e.g. route change while modal is open).
	// Reads suppressInitialFocus via ref to avoid stale closure without adding it as a dep.
	useEffect(
		() => {
			if (!suppressInitialFocusRef.current) {
				chatInputRef.current?.focus();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversationId],
	);
	const shouldBeCentered = fullScreen && stagingAreaMode !== 'inline';

	const chatPromptInput = useMemo(
		() => (
			<RovoChatPromptInput
				key={isAgentSelectionEnabled ? conversationId : 'selected-agent-ga-enhancements-disabled'}
				ref={chatInputRef}
				enableSkills={resolvedEnableSkills}
				enableSkillsInStagingArea={isStudioSkillsInStagingAreaEnabled}
				enableSkillsInSpecialistPromptInput={isStudioSkillsInStagingAreaEnabled}
				browserContextSanitizer={browserContextSanitizer}
				editorPopupsMountPoint={props.editorPopupsMountPoint}
				editorPopupsBoundariesElement={props.editorPopupsBoundariesElement}
				shouldRenderQuickActionsInPortal={props.shouldRenderQuickActionsInPortal}
				isConversationAgentic={!isDefaultAgent}
				conversationAgentId={selectedAgent?.id}
				selectedAgentForInputHat={isAgentSelectionEnabled ? selectedAgentForInputHat : undefined}
				isSpaceScopedConversation={isSpaceScopedConversation}
				spaceSourceCount={areActiveSpaceSourcesKnown ? activeSpaceSources.length : undefined}
				spaceSources={activeSpaceSources}
				zeroSourcesAction={zeroSourcesAction}
				onSubmit={onSubmit}
				onAbort={onAbort}
				onSelectedAgentChange={handleSelectedAgentChange}
				enableAgentMentions={isAgentSelectionEnabled}
				disabledAgentTooltip={
					isAgentSelectionEnabled
						? formatMessage(messages.chatInputDisabledAgentTooltip)
						: undefined
				}
				useCurrentPageContext={useCurrentPageContextForInput}
				contentContext={contentContext}
				isDisabled={
					!!activeAction?.customFollowUpContext
						? !activeAction?.customFollowUpContext?.available
						: props.isDisabled
				}
				isAgentTyping={isAgentTyping}
				hasQueuedMessages={messageQueue.length > 0 && fg('rovo_chat_message_queue')}
				arrowKeyPluginConfigMessages={userMessagesForEditor}
				autocompleteContext={autocompleteContext}
				placeholderOverride={placeholderOverride}
				initialPromptText={initialPromptText}
				shouldSeedInitialPromptText={shouldSeedInitialPromptText}
				promptDraftAndModeKey={conversationId}
				clearModeOnSubmit={conversation?.id === EMPTY_CONVERSATION_ID}
				productKey={productKey}
				siteId={siteId}
				orgId={orgId}
				assistanceServiceParams={assistanceServiceParams}
				shouldBeCentered={shouldBeCentered}
				onPromptChange={onPromptChange}
				minHeight={shouldMinimizeInputHeight ? 95 : fullScreen ? 86 : 56}
				maxHeight={shouldMinimizeInputHeight ? 95 : undefined}
				enableVoiceDictation={props.enableVoiceDictation}
				dictationOverlayBackground={props.dictationOverlayBackground}
				entryPoint={CHAT_INPUT_INTERACTION_SOURCE}
				mode={props.mode}
				showChatModeMenu={elements?.chatModeMenuButton}
				enableMaxMode={enableMaxMode}
				// Opt the main Rovo Chat fly-out (and any other surface that mounts
				// ConversationAssistantRender, e.g. the CwR staging-area chat panel)
				// into apps discovery UI (limited unconnected apps, "Discover more
				// apps" footer). Other surfaces that mount RovoChatPromptInput
				// directly (embedded studio chat, blank object experience, AIFC
				// onboarding, etc.) opt in individually at their own call sites.
				enableAppsDiscoveryUi
				autoSuggestConfig={
					shouldUseTypeahead ? { source: AUTOSUGGEST_SOURCE.ROVO_CHAT, position: 'top' } : undefined
				}
				urlValidationConfig={promptInputDroppableUrls}
				enableBrowserContextBanner={enableBrowserContextBanner}
				aiFeatureContextOverride={aiFeatureContextOverride}
				showContextBannerForEntityContextOverride={smartLink3PPostAuthIsActive}
				actionsMenuItemsVisibility={{ fileUpload: elements?.fileUploadButton }}
				onDiscoverSkillsClick={
					fullScreen || !isDefaultAgent ? undefined : props.onDiscoverSkillsClick
				}
				onBrowsePromptLibraryClick={fullScreen ? undefined : props.onBrowsePromptLibraryClick}
				onCreateSpaceClick={props.onCreateSpaceClick}
				onAutoSuggestQueryChange={
					shouldEvaluateTypeaheadExperiment && isDefaultAgent && !activeAction
						? props.onAutoSuggestQueryChange
						: undefined
				}
				showActionsButton={props.showActionsButton}
				shouldFocusOnMount={!suppressInitialFocus}
			/>
		),
		[
			aiFeatureContextOverride,
			smartLink3PPostAuthIsActive,
			props.isDisabled,
			props.enableVoiceDictation,
			props.dictationOverlayBackground,
			props.editorPopupsMountPoint,
			props.editorPopupsBoundariesElement,
			props.shouldRenderQuickActionsInPortal,
			props.onDiscoverSkillsClick,
			props.onBrowsePromptLibraryClick,
			props.onCreateSpaceClick,
			props.onAutoSuggestQueryChange,
			isDefaultAgent,
			selectedAgent?.id,
			isAgentSelectionEnabled,
			selectedAgentForInputHat,
			handleSelectedAgentChange,
			onSubmit,
			onAbort,
			elements?.chatModeMenuButton,
			enableMaxMode,
			contentContext,
			isAgentTyping,
			userMessagesForEditor,
			autocompleteContext,
			placeholderOverride,
			conversationId,
			conversation?.id,
			formatMessage,
			productKey,
			siteId,
			orgId,
			assistanceServiceParams,
			onPromptChange,
			shouldUseTypeahead,
			shouldBeCentered,
			shouldMinimizeInputHeight,
			promptInputDroppableUrls,
			enableBrowserContextBanner,
			browserContextSanitizer,
			initialPromptText,
			shouldSeedInitialPromptText,
			elements?.fileUploadButton,
			fullScreen,
			shouldEvaluateTypeaheadExperiment,
			activeAction,
			isSpaceScopedConversation,
			areActiveSpaceSourcesKnown,
			activeSpaceSources,
			zeroSourcesAction,
			suppressInitialFocus,
			messageQueue.length,
			isStudioSkillsInStagingAreaEnabled,
			resolvedEnableSkills,
			props.showActionsButton,
			props.mode,
			useCurrentPageContextForInput,
		],
	);

	return (
		<>
			<UFOCustomData data={{ isConversationAgentic: !isDefaultAgent }} />
			{fg('rovo-ext_sbs_enabled') && (
				<SBSTriggerEffect
					productKey={productKey}
					experienceId={experienceId}
					siteId={siteId}
					interceptSubmitRef={sbsInterceptSubmitRef}
				/>
			)}
			<Box>
				{messageQueue.length > 0 && fg('rovo_chat_message_queue') && (
					<QueuedMessageBanner
						items={messageQueue.map((queuedMessage) => ({
							id: queuedMessage.id,
							preview: queuedMessage.preview,
							onRemove: () => {
								setMessageQueue((prev) =>
									prev.filter((message) => message.id !== queuedMessage.id),
								);
							},
						}))}
					/>
				)}
				{fullScreen && conversation?.id === EMPTY_CONVERSATION_ID ? (
					<FadeIn
						key="chat-input"
						duration="large"
						entranceDirection="bottom"
						exitDirection="bottom"
						isPaused={!fullscreenInputStaggerAnimation.shouldAnimate}
					>
						{(animationProps) => <div {...animationProps}>{chatPromptInput}</div>}
					</FadeIn>
				) : (
					chatPromptInput
				)}
			</Box>
		</>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedChatInput = memo(ChatInputComponent, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to the chat-input UI fields
// via the narrow composite selector so the component only re-renders when one of
// those fields changes, rather than on every UI-store state change.
const ChatInputWithSelector = (props: ChatInputProps): JSX.Element => {
	di(useChatInputUISelector);
	const [{ selectedSpace, isChatOnboardingMiniModalOpen, smartLink3PPostAuth }] =
		useChatInputUISelector();
	return (
		<MemoizedChatInput
			{...props}
			selectedSpace={selectedSpace}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			smartLink3PPostAuth={smartLink3PPostAuth}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const ChatInputWithBroadStore = (props: ChatInputProps): JSX.Element => {
	di(useUIStore);
	const { selectedSpace, isChatOnboardingMiniModalOpen, smartLink3PPostAuth } = useUIStore();
	return (
		<ChatInputComponent
			{...props}
			selectedSpace={selectedSpace}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			smartLink3PPostAuth={smartLink3PPostAuth}
		/>
	);
};

export const ChatInput = (props: ChatInputProps): JSX.Element => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<ChatInputWithSelector {...props} />
	) : (
		<ChatInputWithBroadStore {...props} />
	);
};
